import { useState } from 'react';
import {
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Copy,
  Pencil,
  PowerOff,
  RotateCcw,
  StickyNote,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { apiFetch } from '@/lib/api';
import {
  installationMetadataListSchema,
  type InstallationMetadataListItem,
} from '@/schemas/installations';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { RelativeTime } from './relative-time';
import InstallationMetadataDialog from './installation-metadata-dialog';
import DeactivateDeviceDialog from './deactivate-device-dialog';
import ReactivateDeviceDialog from './reactivate-device-dialog';
import { copyToClipboard, formatBytes } from '@/lib/format';
import { useTranslation } from 'react-i18next';
import type { deviceActivationItemSchema } from '@/schemas/dashboard';
import type { z } from 'zod';

type DeviceActivationItem = z.infer<typeof deviceActivationItemSchema>;

interface LicenseActivationsTableProps {
  activations: DeviceActivationItem[];
  /** Licence the seats belong to — the key of the metadata list query. */
  licenseId: string;
  total: number;
  page: number;
  pages: number;
  onPageChange: (page: number) => void;
}

const ID_TRUNCATE_LEN = 16;

/** `GET /api/installations?license_id=<id>` — the editable facts of every named seat. */
async function fetchInstallationMetadata(
  licenseId: string
): Promise<InstallationMetadataListItem[]> {
  const resp = await apiFetch(`/installations?license_id=${encodeURIComponent(licenseId)}`);
  const json = await resp.json();
  return installationMetadataListSchema.parse(json).data;
}

export function LicenseActivationsTable({
  activations,
  licenseId,
  total,
  page,
  pages,
  onPageChange,
}: LicenseActivationsTableProps) {
  const { t } = useTranslation();
  // The metadata list is refreshed by the editor dialog on save (same query key),
  // so a freshly named robot appears in the row without a page reload.
  const metadataQuery = useQuery({
    queryKey: ['license', 'installation-metadata', licenseId],
    queryFn: () => fetchInstallationMetadata(licenseId),
    staleTime: 30_000,
  });
  const metadataById = new Map((metadataQuery.data ?? []).map((meta) => [meta.activationId, meta]));
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [releasingId, setReleasingId] = useState<string | null>(null);
  const [reactivatingId, setReactivatingId] = useState<string | null>(null);
  const reactivatingSeat = activations.find((act) => act.id === reactivatingId);

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <section>
      <h2 className="mb-5 text-xl font-semibold tracking-tight text-foreground">
        {t('licenses.detail.devices.title')}
      </h2>

      <Table className="min-w-[680px] md:min-w-0">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="h-10 w-8 px-2"></TableHead>
            <TableHead className="h-10 px-3 text-[13px] font-medium text-faint">
              {t('licenses.detail.devices.device')}
            </TableHead>
            <TableHead className="h-10 px-3 text-[13px] font-medium text-faint">
              {t('licenses.detail.devices.machineId')}
            </TableHead>
            <TableHead className="h-10 px-3 text-[13px] font-medium text-faint">
              {t('licenses.detail.devices.location')}
            </TableHead>
            <TableHead className="h-10 px-3 text-[13px] font-medium text-faint">
              {t('licenses.detail.devices.status')}
            </TableHead>
            <TableHead className="h-10 px-3 text-[13px] font-medium text-faint">
              {t('licenses.detail.devices.activated')}
            </TableHead>
            <TableHead className="h-10 px-3 text-[13px] font-medium text-faint">
              {t('licenses.detail.devices.lastSeen')}
            </TableHead>
            <TableHead className="h-10 w-10 px-3">
              <span className="sr-only">{t('licenses.detail.devices.actions')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {activations.map((act) => {
            const rowId = act.id;
            const isExpanded = expanded.has(rowId);
            const truncLen = ID_TRUNCATE_LEN;
            const displayId = isExpanded
              ? act.fingerprintHash
              : `${act.fingerprintHash.slice(0, truncLen)}…`;
            const isActive = !act.deactivatedAt;
            const meta = metadataById.get(rowId);

            return (
              <TableRow key={rowId} className="hover:bg-transparent">
                <TableCell className="px-2 py-3">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => toggleExpanded(rowId)}
                    aria-label={
                      isExpanded
                        ? t('licenses.detail.devices.collapseAria')
                        : t('licenses.detail.devices.expandAria')
                    }
                    aria-expanded={isExpanded}
                    data-testid={`expand-device-${rowId}`}
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                </TableCell>
                <TableCell className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <div className="min-w-0">
                      {meta?.label ? (
                        <div
                          className="truncate text-[13px] font-medium text-foreground"
                          data-testid={`device-label-${rowId}`}
                        >
                          {meta.label}
                        </div>
                      ) : null}
                      <code className="whitespace-nowrap font-mono text-[13px] text-muted-foreground">
                        {act.installationId ?? '—'}
                      </code>
                    </div>
                    {meta?.notes ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className="shrink-0 text-muted-foreground"
                              aria-label={t('licenses.detail.devices.notesAria')}
                              data-testid={`device-notes-${rowId}`}
                            >
                              <StickyNote className="h-3.5 w-3.5" aria-hidden="true" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[280px] whitespace-pre-wrap">
                            {meta.notes}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : null}
                  </div>
                </TableCell>
                {/* Columns must match the header order: Device | Machine ID | Location. */}
                <TableCell className="px-3 py-3">
                  <div className="flex items-center gap-1">
                    <code
                      className="cursor-pointer whitespace-nowrap font-mono text-[13px] text-muted-foreground hover:text-foreground"
                      onClick={() => toggleExpanded(rowId)}
                      title={t('licenses.detail.devices.clickToExpand')}
                    >
                      {displayId}
                    </code>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={() => copyToClipboard(act.fingerprintHash)}
                      aria-label={t('licenses.detail.devices.copyMachineAria', {
                        id: act.fingerprintHash.slice(0, 8),
                      })}
                      data-testid={`copy-machine-${rowId}`}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap px-3 py-3 text-[13px]">
                  {meta?.location ? (
                    <span className="text-foreground" data-testid={`device-location-${rowId}`}>
                      {meta.location}
                    </span>
                  ) : (
                    <span className="text-muted-foreground/50">—</span>
                  )}
                </TableCell>
                <TableCell className="px-3 py-3">
                  <span
                    className={`inline-flex items-center gap-2 whitespace-nowrap text-[13px] font-medium ${
                      isActive ? 'text-status-active' : 'text-status-revoked'
                    }`}
                  >
                    <span
                      className={`h-[7px] w-[7px] rounded-full ${
                        isActive ? 'bg-status-active' : 'bg-status-revoked'
                      }`}
                      aria-hidden="true"
                    />
                    {isActive ? t('common.status.active') : t('common.status.deactivated')}
                  </span>
                </TableCell>
                <TableCell className="whitespace-nowrap px-3 py-3 text-[13px] text-muted-foreground">
                  <RelativeTime date={act.activatedAt} fallback="—" />
                </TableCell>
                <TableCell className="whitespace-nowrap px-3 py-3 text-[13px] text-muted-foreground">
                  <RelativeTime date={act.lastSeen} />
                </TableCell>
                <TableCell className="px-3 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {isActive && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => setReleasingId(rowId)}
                        aria-label={t('licenses.detail.devices.deactivate.actionAria', {
                          id: act.installationId ?? act.fingerprintHash.slice(0, 8),
                        })}
                        data-testid={`deactivate-device-${rowId}`}
                      >
                        <PowerOff className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {!isActive && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-status-active"
                        onClick={() => setReactivatingId(rowId)}
                        aria-label={t('licenses.detail.devices.reactivate.actionAria', {
                          id: act.installationId ?? act.fingerprintHash.slice(0, 8),
                        })}
                        data-testid={`reactivate-device-${rowId}`}
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setEditingId(rowId)}
                      aria-label={t('licenses.detail.devices.editMetadataAria', {
                        id: act.installationId ?? act.fingerprintHash.slice(0, 8),
                      })}
                      data-testid={`edit-installation-${rowId}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Expanded device details — rendered outside the table to avoid colspan issues */}
      {activations
        .filter((act) => expanded.has(act.id))
        .map((act) => (
          <DeviceDetailPanel key={`detail-${act.id}`} activation={act} />
        ))}

      <InstallationMetadataDialog
        activationId={editingId}
        licenseId={licenseId}
        onOpenChange={(open) => !open && setEditingId(null)}
      />

      <DeactivateDeviceDialog
        activationId={releasingId}
        licenseId={licenseId}
        onOpenChange={(open) => !open && setReleasingId(null)}
      />

      <ReactivateDeviceDialog
        activationId={reactivatingId}
        licenseId={licenseId}
        revoked={reactivatingSeat?.fingerprintHash.startsWith('revoked:') ?? false}
        onOpenChange={(open) => !open && setReactivatingId(null)}
      />

      {pages > 1 && (
        <div className="mt-4 flex items-center justify-between border-t pt-4">
          <span className="text-[13px] tabular-nums text-faint">
            {t('common.pageOf', { page, pages, total })}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 border-input bg-transparent"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              aria-label={t('common.previousPage')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 border-input bg-transparent"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= pages}
              aria-label={t('common.nextPage')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

// ── Device Detail Panel (shown when row is expanded) ─────────────────────────

function DeviceDetailPanel({ activation }: { activation: DeviceActivationItem }) {
  const { t } = useTranslation();
  const device = activation.device;

  if (!device) {
    return (
      <div className="mt-3 rounded-lg border border-dashed px-4 py-4 text-sm text-muted-foreground">
        {t('licenses.detail.devices.noInventory')}
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-lg border bg-foreground/[0.02] px-4 py-4">
      {/* Two columns on desktop: inventory facts on the left, hardware on the
          right — keeps the expanded row from stretching the page vertically. */}
      <div className="grid gap-x-8 gap-y-5 md:grid-cols-2">
        {/* Left: overview facts + modules */}
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Field label={t('licenses.detail.devices.pcModel')} value={device.pcModel} />
            <Field label={t('licenses.detail.devices.os')} value={device.os} />
            <Field label={t('licenses.detail.devices.ram')} value={formatBytes(device.ramBytes)} />
            <Field
              label={t('licenses.detail.devices.reported')}
              value={
                device.reportedAt ? <RelativeTime date={device.reportedAt} fallback="—" /> : '—'
              }
            />
          </div>

          {/* Modules */}
          {device.modules.length > 0 && (
            <div>
              <h4 className="mb-2 text-[13px] font-semibold text-muted-foreground">
                {t('licenses.detail.devices.modules')}
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {device.modules.map((mod, i) => (
                  <Badge
                    key={`mod-${i}`}
                    variant="outline"
                    className="font-mono text-xs font-normal"
                  >
                    {mod.name ?? 'unknown'}
                    {mod.version ? ` @ ${mod.version}` : ''}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: disks, servos, config, raw fingerprint */}
        <div className="space-y-5">
          {/* Disks */}
          {device.disks.length > 0 && (
            <div>
              <h4 className="mb-2 text-[13px] font-semibold text-muted-foreground">
                {t('licenses.detail.devices.disks')}
              </h4>
              <div className="space-y-1.5">
                {device.disks.map((disk, i) => (
                  <div
                    key={`disk-${i}`}
                    className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border bg-background px-3 py-1.5 text-xs"
                  >
                    <span className="font-mono font-medium">
                      {disk.model ?? t('licenses.detail.devices.unknownModel')}
                    </span>
                    {disk.serial && (
                      <span className="font-mono text-muted-foreground">S/N: {disk.serial}</span>
                    )}
                    {disk.sizeBytes != null && (
                      <span className="tabular-nums text-muted-foreground">
                        {formatBytes(disk.sizeBytes)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Servos */}
          {device.servos.length > 0 && (
            <div>
              <h4 className="mb-2 text-[13px] font-semibold text-muted-foreground">
                {t('licenses.detail.devices.servos')}
              </h4>
              <div className="space-y-1.5">
                {device.servos.map((servo, i) => (
                  <div
                    key={`servo-${i}`}
                    className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border bg-background px-3 py-1.5 text-xs"
                  >
                    <span className="font-mono font-medium">
                      {servo.model ?? t('licenses.detail.devices.unknownModel')}
                    </span>
                    {servo.serial && (
                      <span className="font-mono text-muted-foreground">S/N: {servo.serial}</span>
                    )}
                    {servo.position && (
                      <span className="text-muted-foreground">
                        {t('licenses.detail.devices.position', { position: servo.position })}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Drives */}
          {device.drives.length > 0 && (
            <div>
              <h4 className="mb-2 text-[13px] font-semibold text-muted-foreground">
                {t('licenses.detail.devices.drives')}
              </h4>
              <div className="space-y-1.5">
                {device.drives.map((drive, i) => (
                  <div
                    key={`drive-${i}`}
                    className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border bg-background px-3 py-1.5 text-xs"
                  >
                    {drive.driver && (
                      <span className="text-muted-foreground">
                        {t('licenses.detail.devices.driver')}: {drive.driver}
                      </span>
                    )}
                    <span className="font-mono font-medium">
                      {drive.model ?? t('licenses.detail.devices.unknownModel')}
                    </span>
                    <span className="text-muted-foreground">
                      {t('licenses.detail.devices.axis', { axis: drive.axis ?? i })}
                    </span>
                    {drive.firmware && (
                      <span className="font-mono text-muted-foreground">FW: {drive.firmware}</span>
                    )}
                    {drive.serial && (
                      <span className="font-mono text-muted-foreground">
                        {t('licenses.detail.devices.serial')}: {drive.serial}
                      </span>
                    )}
                    {drive.userId && (
                      <span className="font-mono text-muted-foreground">ID: {drive.userId}</span>
                    )}
                    {drive.configHash && (
                      <span className="font-mono text-muted-foreground">CFG: {drive.configHash}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Robot Config */}
          {device.robotConfig && Object.keys(device.robotConfig).length > 0 && (
            <div>
              <h4 className="mb-2 text-[13px] font-semibold text-muted-foreground">
                {t('licenses.detail.devices.robotConfig')}
              </h4>
              <pre className="max-h-36 overflow-auto whitespace-pre-wrap break-all rounded-md border bg-background p-2.5 font-mono text-xs">
                {JSON.stringify(device.robotConfig, null, 2)}
              </pre>
            </div>
          )}

          {/* Raw Fingerprint */}
          {device.rawFingerprint && Object.keys(device.rawFingerprint).length > 0 && (
            <div>
              <h4 className="mb-2 text-[13px] font-semibold text-muted-foreground">
                {t('licenses.detail.devices.rawFingerprint')}
              </h4>
              <pre className="max-h-36 overflow-auto whitespace-pre-wrap break-all rounded-md border bg-background p-2.5 font-mono text-xs">
                {JSON.stringify(device.rawFingerprint, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <span className="text-[13px] text-faint">{label}</span>
      <p className="mt-1 text-sm font-medium">
        {value ?? <span className="text-muted-foreground">—</span>}
      </p>
    </div>
  );
}
